export type NotificationSeverity = 1 | 2 | 3 | 4

export const SEVERITY = {info: 1, success: 2, warning: 3, error: 4} as const

export type DeliveryChannel = 'inapp' | 'sms' | 'telegram' | 'ticket' | 'push'
export type DeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped' | 'cancelled'

export interface NotificationDelivery {
  channel: DeliveryChannel
  status: DeliveryStatus
  sentAtUtc?: string | null
  error?: string | null
  reference?: string | null
}

export interface NotificationItem {
  id: number
  notificationId: number
  companyId?: number | null
  companyName?: string | null
  groupKey: string
  groupLabel: string
  eventKey: string
  eventLabel: string
  severity: NotificationSeverity
  title: string
  body?: string | null
  linkPath?: string | null
  isRead: boolean
  readAtUtc?: string | null
  createdAtUtc: string
  occurrenceCount: number
  lastOccurredAtUtc?: string | null
  resolvedAtUtc?: string | null
  deliveries?: NotificationDelivery[] | null
}

export interface NotificationSummary {
  available: boolean
  unreadCount: number
  latest: NotificationItem[]
}

export interface NotificationList {
  items: NotificationItem[]
  total: number
  page: number
  pageSize: number
  unreadCount: number
}

export interface NotificationListFilter {
  page: number
  pageSize: number
  unreadOnly: boolean
  companyId?: number | null
  group?: string | null
}

/** Channel level per event group: off, everything, warnings and errors, errors only. */
export type ChannelLevel = 'off' | 'all' | 'warnings' | 'errors'

export interface NotificationEventDefinition {
  key: string
  label: string
  defaultSeverity: NotificationSeverity
  ticketEligible: boolean
}

/**
 * A group whose SMS is a business service's own paid SMS (the notification centre never texts it): the page
 * links to the service instead of offering an SMS level. `path` may hold `{cpi}`, the selected business.
 */
export interface SmsByService {
  label: string
  path: string
}

export interface NotificationGroup {
  key: string
  label: string
  description: string
  icon: string
  companyScoped: boolean
  events: NotificationEventDefinition[]
  smsByService?: SmsByService | null
  /**
   * A business's group texted only while the business has «پیامک اعلان‌ها» on, each SMS paid from its wallet (backend
   * 1.0.0.738+): the page keeps the SMS level and says so, with a link to the service.
   */
  smsPaidByBusiness?: SmsByService | null
}

export interface NotificationPreference {
  groupKey: string
  sms: ChannelLevel
  telegram: ChannelLevel
  /** Web push to the user's browsers (push-worker.js); absent from servers before it existed. */
  push?: ChannelLevel
}

export interface QuietHours {
  enabled: boolean
  start: string
  end: string
}

export interface ChannelState {
  available: boolean
  connected: boolean
  destination?: string | null
  hint?: string | null
  setupPath?: string | null
  /** Telegram only: the bot the connection link opens, without «@» (ASATSPbot). */
  bot?: string | null
}

export interface CompanyUser {
  userId: number
  name: string
  mobile?: string | null
  isManager: boolean
}

export interface CompanyGroupSetting {
  groupKey: string
  autoTicketEnabled: boolean
  autoTicketAvailable: boolean
  usesDefaultRecipients: boolean
  recipientUserIds: number[]
}

export interface CompanySettings {
  companyId: number
  companyName: string
  canManage: boolean
  users: CompanyUser[]
  defaultRecipientUserIds: number[]
  groups: CompanyGroupSetting[]
}

export interface NotificationSettings {
  groups: NotificationGroup[]
  preferences: NotificationPreference[]
  quietHours: QuietHours
  sms: ChannelState
  telegram: ChannelState
  /** destination: how many of the user's devices receive push. */
  push?: ChannelState | null
  company?: CompanySettings | null
}

export interface UserSettingsUpdate {
  preferences: NotificationPreference[]
  quietHours: QuietHours
}

export interface CompanySettingsUpdate {
  companyId: number
  groups: Array<
    Pick<
      CompanyGroupSetting,
      'groupKey' | 'autoTicketEnabled' | 'usesDefaultRecipients' | 'recipientUserIds'
    >
  >
}

export interface NotificationApiError extends Error {
  code?: string
  status?: number
}
